/**
 * Burst detection display module.
 *
 * Data flow:
 *   /ws → ``bursts_active`` message → handleBurstsUpdate(bursts)
 *     ↳ on connect: backend sends initial set
 *     ↳ every 60s: BurstDetectionJob broadcasts after each detection cycle
 *     ↳ on burst end: same broadcast carries the updated (shorter) list
 *
 * The polling endpoint ``GET /api/bursts/active`` survives as a
 * fallback (called once at init and by push-notification arrivals via
 * ``checkForBursts``). The 5-minute interval poll that used to drive
 * this module is gone.
 *
 * Dismissed-bursts state persists in ``sessionStorage`` so a refresh
 * doesn't bring back a banner the user just closed. Hydration runs
 * at module-evaluation time (not inside ``initBurstDetection``) so
 * the first ``bursts_active`` message — which can arrive within tens
 * of milliseconds — already filters against dismissed scopes.
 */

import { setActiveBursts } from './state.js';
import { updateBurstCircles } from './map.js';
import { escapeHtml, getBurstUrl } from './utils.js';

// How long a dismissed burst stays hidden after the user clicks ×.
// Dismissal expires so a burst that's still active after this much
// time gets a chance to re-surface — common when the user dismissed
// before realising it was a major event.
const DISMISS_DURATION_MS = 15 * 60 * 1000;

// Where to persist the dismissed map between page loads in the same
// tab. sessionStorage (not localStorage) so opening a new tab clears
// it — that's the natural "fresh look" surface.
const DISMISS_STORAGE_KEY = 'burst.dismissed.v1';

// scope -> timestamp(ms) when dismissed. Hydrated from sessionStorage
// on init; persisted on every dismiss.
const dismissedBursts = new Map();

// How often we refresh the trending fallback items.
const TRENDING_FETCH_INTERVAL = 10 * 60 * 1000;

// DOM elements
let bannerEl = null;
let textEl = null;
let dismissBtn = null;
let iconEl = null;

// Dismiss key of whatever the banner currently shows (a burst scope, or
// `trending:{url}`), and whether it's a burst (bursts outrank trending).
let currentScope = null;
let currentIsBurst = false;

// Count-free trending items (bursts + cities) used as the banner fallback
// when no active burst is showing. Same surface, same dismiss machinery.
let trendingItems = [];


/**
 * Read the persisted dismissal map. Returns an empty Map on parse
 * errors so a corrupt storage entry doesn't crash the module.
 */
function loadDismissed() {
    try {
        const raw = sessionStorage.getItem(DISMISS_STORAGE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return;
        const now = Date.now();
        for (const [scope, ts] of Object.entries(parsed)) {
            // Drop already-expired entries on hydrate to keep the map tight.
            if (typeof ts === 'number' && now - ts < DISMISS_DURATION_MS) {
                dismissedBursts.set(scope, ts);
            }
        }
    } catch (e) {
        // sessionStorage can throw in privacy modes; fail open.
        console.warn('burst dismiss storage load failed:', e);
    }
}


function persistDismissed() {
    try {
        const obj = Object.fromEntries(dismissedBursts);
        sessionStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify(obj));
    } catch (e) {
        console.warn('burst dismiss storage save failed:', e);
    }
}


/**
 * Initialize burst banner DOM hooks. Idempotent — calling it twice
 * is a no-op because the same elements are looked up by id.
 *
 * The actual data feed is the ``bursts_active`` WebSocket message
 * — see app.js for the dispatch. We also fire a one-shot fetch here
 * in case WS hasn't connected yet on first load.
 */
export function initBurstDetection() {
    bannerEl = document.getElementById('burst-banner');
    textEl = document.getElementById('burst-text');
    dismissBtn = document.getElementById('burst-dismiss');
    iconEl = document.getElementById('burst-icon');

    if (!bannerEl || !textEl) {
        console.warn('Burst banner elements not found');
        return;
    }

    if (dismissBtn) {
        dismissBtn.addEventListener('click', dismissCurrent);
    }

    // Race-free first paint: WS will deliver initial bursts on
    // connect, but if the handshake is slow we don't want the map
    // sitting without circles. One-shot poll fills that gap; if WS
    // arrives first, its data is at least as fresh.
    checkForBursts();

    // Trending fallback for the same banner (shown only when no active
    // burst is). Refreshed on a slow cadence — it's a "nice to know", not
    // an alert.
    fetchTrendingBanner();
    setInterval(fetchTrendingBanner, TRENDING_FETCH_INTERVAL);
}


/**
 * Hand a fresh bursts array off to the display layer.
 *
 * Called by the WS dispatcher in app.js and by checkForBursts(). Pure
 * function w.r.t. the input — same input always produces the same UI
 * state.
 */
export function handleBurstsUpdate(bursts) {
    if (!Array.isArray(bursts)) return;

    setActiveBursts(bursts);
    updateBurstCircles();

    // The map carries the full 24h display set (live + recently-ended
    // stories), but the banner is a single-slot "happening now" device —
    // only a live burst should claim it as a "⚠️" alert. Ended stories live
    // on the map; when nothing is live, fall through to the trending pill.
    // Server orders live-first, so [0] of the live subset is the top burst.
    const liveBursts = bursts.filter(b => b.is_active !== false && !isDismissed(b.scope));

    if (liveBursts.length > 0) {
        showBurst(liveBursts[0]);
    } else {
        renderTrendingFallback();
    }
}


/**
 * Fetch the count-free trending items (bursts + cities) used as the banner
 * fallback. Repaints the fallback only when a burst isn't occupying the
 * banner — bursts always outrank trending.
 */
async function fetchTrendingBanner() {
    try {
        const response = await fetch('/api/trending/now');
        if (!response.ok) return;
        const data = await response.json();
        trendingItems = data.available ? (data.items || []) : [];
    } catch (error) {
        console.warn('Trending banner fetch failed:', error);
        return;
    }
    if (!currentIsBurst) renderTrendingFallback();
}


/** Show the top non-dismissed trending item, or hide the banner. */
function renderTrendingFallback() {
    const item = trendingItems.find(it => !isDismissed(`trending:${it.url}`));
    if (item) {
        showTrending(item);
    } else {
        hideBanner();
    }
}


/**
 * Polling fallback, used at init.
 * Kept around because the WS isn't always available — proxy
 * misconfig, hostile networks, etc.
 */
async function checkForBursts() {
    try {
        const response = await fetch('/api/bursts/active');
        if (!response.ok) {
            console.warn('Burst API returned', response.status);
            return;
        }
        const data = await response.json();
        handleBurstsUpdate(data.bursts || []);
    } catch (error) {
        console.error('Error checking for bursts:', error);
    }
}


function isDismissed(scope) {
    const dismissTime = dismissedBursts.get(scope);
    if (!dismissTime) return false;
    if (Date.now() - dismissTime > DISMISS_DURATION_MS) {
        dismissedBursts.delete(scope);
        persistDismissed();
        return false;
    }
    return true;
}


function showBurst(burst) {
    if (!bannerEl || !textEl) return;

    const message = formatBurstMessage(burst);
    if (!message) {
        hideBanner();
        return;
    }
    currentScope = burst.scope;
    currentIsBurst = true;
    if (iconEl) iconEl.textContent = '⚠️';
    textEl.innerHTML = message;

    // Single accent for every burst — severity classification is no longer
    // surfaced. No inline --burst-color: the CSS falls back to --burst-accent,
    // which follows the light/dark theme where a copied hex would not.
    bannerEl.classList.remove('hidden');
}


function showTrending(item) {
    if (!bannerEl || !textEl) return;
    currentScope = `trending:${item.url}`;
    currentIsBurst = false;
    if (iconEl) iconEl.textContent = '↗';

    const title = escapeHtml(item.title || '');
    textEl.innerHTML =
        `<a href="${encodeURI(item.url)}" class="burst-link">` +
        `<strong>Nu trending</strong> — ${title}</a>`;

    bannerEl.classList.remove('hidden');
}


function hideBanner() {
    if (!bannerEl) return;
    bannerEl.classList.add('hidden');
    currentScope = null;
    currentIsBurst = false;
}


function dismissCurrent() {
    const wasTrending = !currentIsBurst;
    if (currentScope) {
        dismissedBursts.set(currentScope, Date.now());
        persistDismissed();
    }
    hideBanner();
    // Dismissing a trending pill reveals the next eligible one. Dismissing a
    // burst just hides — the next WS cycle re-evaluates bursts (unchanged
    // behaviour), so we don't pop a trending pill over a live burst.
    if (wasTrending) renderTrendingFallback();
}


function formatBurstMessage(burst) {
    const label = escapeHtml(burst.scope_label || burst.scope || '');
    const count = burst.current_count || 0;
    const minutes = burst.window_minutes || 0;
    const url = getBurstUrl(burst);

    let text;
    if (burst.scope.startsWith('city:')) {
        text = `<strong>${label}</strong>: ${count} meldingen in ${minutes} min`;
    } else if (burst.scope.startsWith('service:')) {
        text = `<strong>${label}</strong> landelijk: ${count} meldingen`;
    } else {
        text = escapeHtml(burst.message || '') || `${count} meldingen`;
    }

    return `<a href="${encodeURI(url)}" class="burst-link">${text}</a>`;
}


// Exported for manual triggering (e.g., from push-notification arrival in web-push.js)
export { checkForBursts };


// ── Module-top side effects ────────────────────────────────────────
//
// Both of these run at import time. They must run BEFORE app.js
// connects the WebSocket, because:
//
// * ``window.__handleBurstsUpdate`` is read by the WS ``onmessage``
//   handler in app.js. If it's still undefined when the first
//   ``bursts_active`` message arrives — and the WS handshake is
//   ~1-10 ms while the rest of app.js takes microseconds — the
//   message is silently dropped. Setting at module-eval time
//   guarantees it's ready before connect() runs (ES module imports
//   are hoisted, so this whole file is evaluated before app.js's
//   first synchronous line).
//
// * ``loadDismissed()`` populates the dismissed-bursts Map from
//   sessionStorage. Calling it from inside ``initBurstDetection``
//   (which the old code did under a 3 s setTimeout) meant the first
//   ``bursts_active`` message could fail the dismissed filter and
//   flash a banner the user already closed in a previous load.
//   Pure sessionStorage read; safe to do before DOM is ready.

loadDismissed();

if (typeof window !== 'undefined') {
    window.__handleBurstsUpdate = handleBurstsUpdate;
}
